// The corpus — real conversations lifted out of another environment so a tagger
// can be tried against what clients actually say.
//
// WHY THIS EXISTS. A tag is worded against real language, and the only place real
// language happens is production. Dev conversations are ours: short, written by
// whoever was testing, and nothing like a client's. A tag tuned against them
// works beautifully on staff and badly on the people it was written for. So an
// operator exports projects from an environment they can read, drops the file
// here, and runs any tagger over the result before turning a tag on anywhere.
//
// UPLOADING IS CHUNKED, and that is not an implementation detail the reader
// should have to think about: a five-hundred-project export is a body no proxy
// wants, so this splits it and passes the batch id back each time. The reader
// drops one file and watches one progress line.
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, useToast } from "@bi/shared";
import type { CorpusProjectUpload } from "@bi/shared/types";

import { ErrorState, LoadingState, errorMessage } from "../components/states";
import { deleteCorpus, uploadCorpus } from "../lib/api";
import { corpusQuery, taggingIdentityQuery, taggingKeys } from "../lib/queries";

/** Mirrors `MAX_CORPUS_PROJECTS_PER_REQUEST` in `web/routes/bi_tagging.py`. The
 *  server refuses more; this is what keeps it from ever having to. */
const CHUNK = 100;

/** Read whatever shape the file is in and return projects.
 *
 *  Deliberately forgiving about the ENVELOPE — a bare array, `{projects: [...]}`,
 *  or newline-delimited JSON all mean the same thing to a person, and refusing
 *  one of them teaches nothing. It is strict about the CONTENT: a project needs
 *  an id and something to read. */
function parseCorpusFile(text: string): CorpusProjectUpload[] {
  const trimmed = text.trim();
  if (!trimmed) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    // NDJSON: one project per line, which is what a `jq -c` export produces.
    parsed = trimmed
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => JSON.parse(line));
  }
  const list = Array.isArray(parsed)
    ? parsed
    : ((parsed as { projects?: unknown[] })?.projects ?? []);
  return list as CorpusProjectUpload[];
}

export function CorpusPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const batches = useQuery(corpusQuery);
  const me = useQuery(taggingIdentityQuery);
  const canWrite = me.data?.canWrite === true;

  const [label, setLabel] = useState("");
  const [sourceEnv, setSourceEnv] = useState("prod");
  const [over, setOver] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const projects = parseCorpusFile(await file.text());
      if (projects.length === 0) throw new Error("No projects found in that file.");
      let batchId: string | null = null;
      let accepted = 0;
      const rejected: string[] = [];
      for (let i = 0; i < projects.length; i += CHUNK) {
        const slice = projects.slice(i, i + CHUNK);
        setProgress(
          `Uploading ${Math.min(i + slice.length, projects.length)} of ${projects.length}…`,
        );
        const ack = await uploadCorpus({
          label: label.trim() || file.name,
          sourceEnv: sourceEnv.trim(),
          batchId,
          projects: slice,
        });
        batchId = ack.batch.id;
        accepted += ack.accepted;
        rejected.push(...ack.rejected);
      }
      return { total: projects.length, accepted, rejected };
    },
    onSuccess: (r) => {
      setProgress(null);
      toast.add({
        title: `${r.accepted} of ${r.total} projects uploaded.`,
        // Skipped projects are NAMED, not summarised into a count: "12 rejected"
        // tells the reader nothing they can act on.
        description: r.rejected.length
          ? `Skipped: ${r.rejected.slice(0, 5).join("; ")}`
          : undefined,
      });
      void qc.invalidateQueries({ queryKey: taggingKeys.corpus });
    },
    onError: (e) => {
      setProgress(null);
      toast.add({ title: errorMessage(e), data: { tone: "danger" } });
    },
  });

  const drop = useMutation({
    mutationFn: (batchId: string) => deleteCorpus(batchId),
    onSuccess: () => {
      toast.add({ title: "Batch deleted, with everything tagged from it." });
      void qc.invalidateQueries({ queryKey: taggingKeys.corpus });
    },
    onError: (e) => toast.add({ title: errorMessage(e), data: { tone: "danger" } }),
  });

  if (batches.isPending) return <LoadingState label="Loading corpora…" />;
  if (batches.isError) {
    return <ErrorState error={batches.error} onRetry={() => void batches.refetch()} />;
  }

  return (
    <div className="bi-page">
      <div className="bi-page__head">
        <h1 className="bi-page__title">Corpus</h1>
        <p className="bi-page__sub">
          Conversations uploaded from another environment, so a tag can be tried against what
          clients actually say before it is turned on. Nothing here is a project: it is evidence,
          parked, and it expires.
        </p>
      </div>

      {canWrite ? (
        <div className="bi-panel">
          <div className="bi-panel__head">
            <div>
              <h2 className="bi-panel__title">Upload</h2>
              <p className="bi-panel__note">
                A JSON array, a <code>{"{ projects: [...] }"}</code> object, or one project per
                line. Large files are split and uploaded as one batch.
              </p>
            </div>
          </div>
          <div className="bi-panel__body">
            <div className="bi-form">
              <div className="bi-form__row">
                <label className="bi-field bi-field--grow">
                  <span className="bi-field__label">Label</span>
                  <input
                    className="bi-input"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="Defaults to the file name"
                  />
                </label>
                <label className="bi-field">
                  <span className="bi-field__label">Came from</span>
                  <input
                    className="bi-input"
                    value={sourceEnv}
                    onChange={(e) => setSourceEnv(e.target.value)}
                  />
                </label>
              </div>

              <div
                className={over ? "bi-drop bi-drop--over" : "bi-drop"}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOver(true);
                }}
                onDragLeave={() => setOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setOver(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file) upload.mutate(file);
                }}
                onClick={() => fileRef.current?.click()}
              >
                <span className="bi-drop__title">
                  {progress ?? (upload.isPending ? "Uploading…" : "Drop a JSON export here")}
                </span>
                <span className="bi-drop__hint">
                  Each project needs an id and either messages or threads. A project this cannot
                  read is skipped and named, never raised — one bad entry must not cost the rest of
                  the file.
                </span>
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/json,.json,.ndjson,.jsonl"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) upload.mutate(file);
                    e.target.value = "";
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="bi-panel">
        <div className="bi-panel__head">
          <h2 className="bi-panel__title">Batches</h2>
          <span className="bi-panel__note">Pick one on a tagger's run form to tag it.</span>
        </div>
        {batches.data.length === 0 ? (
          <div className="bi-empty">Nothing uploaded yet.</div>
        ) : (
          <div className="bi-table-wrap">
            <table className="bi-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>From</th>
                  <th className="bi-table__num">Projects</th>
                  <th className="bi-table__num">Messages</th>
                  <th>Uploaded</th>
                  <th>Expires</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {batches.data.map((b) => (
                  <tr key={b.id}>
                    <td>
                      <div>{b.label}</div>
                      <div className="bi-field__hint">{b.uploadedBy}</div>
                    </td>
                    <td>
                      <Badge size="sm" tone="neutral" variant="soft">
                        {b.sourceEnv || "unknown"}
                      </Badge>
                    </td>
                    <td className="bi-table__num">{b.nProjects}</td>
                    <td className="bi-table__num">{b.nMessages}</td>
                    <td>{b.createdAt.slice(0, 10)}</td>
                    <td>{b.expiresAt.slice(0, 10)}</td>
                    <td>
                      {canWrite ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          tone="danger"
                          onClick={() => drop.mutate(b.id)}
                        >
                          Delete
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="bi-panel__foot">
          A batch holds a real client's words, so it expires and the worker sweeps it. Deleting one
          takes its findings with it.
        </div>
      </div>
    </div>
  );
}
